import { memo, useCallback } from "react";
import * as Bananas from "bananas-commerce-admin";

import { Box, SxProps, Theme } from "@mui/material";

import {
  ObituarySymbol,
  ObituarySymbolOrImage,
} from "@/extensions/obituary/types";

export interface ImageCardProps extends Omit<Bananas.CardProps, "onClick"> {
  selected?: boolean;
  symbol: ObituarySymbolOrImage;
  onClick: (symbol: ObituarySymbolOrImage) => void;
}

const styles = {
  paper: {
    cursor: "pointer",
    "&:hover": { borderColor: "grey.800" },
    overflow: "hidden",
    bgcolor: "white",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
  },

  header: {
    p: 1,
    bgcolor: "grey.50",
    borderRadius: "0 0 4px 4px",
    height: "100%",
    minHeight: 250,
    backgroundColor: "white",
    display: "flex",
    flexDirection: "column",
  },

  title: {
    fontSize: 13,
  },

  figure: {
    m: 2,
    flexGrow: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  img: {
    width: "100%",
    maxHeight: 150,
    minHeight: 50,
    objectFit: "contain",
  },

  text: {
    borderTopWidth: 1,
    borderTopStyle: "solid",
    borderTopColor: "divider",
    alignItems: "flex-start",
    bgcolor: "background.paper",

    "& p": {
      fontSize: 14,
    },

    "& span:nth-of-type(2)": {
      fontSize: 11,
    },
  },

  selected: {
    boxShadow: (theme: Theme) =>
      `inset 0 0 0 6px ${theme.palette.primary.light}`,
    borderColor: "grey.800",
    "&:hover": { borderColor: "grey.800" },
    "& span": { color: "#ffffffff" },
    opacity: 1,
  },

  selectedHeader: {
    bgcolor: "primary.light",
  },
};

function isObituarySymbol(
  symbol: ObituarySymbolOrImage,
): symbol is ObituarySymbol {
  return "file_url" in symbol;
}

const ImageCard: React.FC<ImageCardProps> = ({
  onClick,
  selected,
  symbol,
  sx,
  ...props
}) => {
  const isSymbol = isObituarySymbol(symbol);

  const handleOnClick = useCallback(() => {
    onClick?.(symbol);
  }, [onClick, symbol]);

  return (
    <Bananas.Card
      gridProps={{ height: "100%", display: "flex", flexDirection: "column" }}
      role="button"
      sx={
        { ...sx, ...(selected && styles.selected), ...styles.paper } as SxProps
      }
      onClick={handleOnClick}
      {...props}
    >
      <Box component="figure" sx={styles.figure}>
        <Box
          component="img"
          src={isSymbol ? symbol.file_url : symbol.url}
          sx={styles.img}
        />
      </Box>
      {isSymbol && (
        <Bananas.CardHeader
          subheader={`${symbol?.reference} • ${symbol?.category}`}
          sx={
            {
              ...styles.text,
              ...(selected && styles.selectedHeader),
            } as SxProps
          }
          title={symbol?.title}
        />
      )}
    </Bananas.Card>
  );
};

export default memo(ImageCard);
